import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { s } from '../../components/PeriodStatsPage.styles';
import * as XLSX from 'xlsx';
import GridTable, { GridItem } from '../GridTable';
import SimpleInfoTable, { SimpleItem } from '../SimpleInfoTable';

interface CourtInfoData {
  name: string;
  data: (string | number)[];
  isPremium?: boolean;
}

interface CourtInfoProps {
  data?: CourtInfoData | null;
  isPremium?: boolean;
}

export default function CourtInfo({ data, isPremium = false }: CourtInfoProps) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (data) {
      const timer = setTimeout(() => setIsLoading(false), 200);
      return () => clearTimeout(timer);
    }
  }, [data]);

  if (!data) return null;

  const { name, data: daysArray = [] } = data;

  // 0번 인덱스는 평균
  const avgGap = Number(daysArray[0]) || null;

  // 홀수 인덱스 = 매각, 짝수 인덱스 = 배당
  const 매각 = daysArray
    .map((v, i) => (i % 2 === 1 ? Number(v) : null))
    .filter((v) => v !== null && !isNaN(v));

  const 배당 = daysArray
    .map((v, i) => (i !== 0 && i % 2 === 0 ? Number(v) : null))
    .filter((v) => v !== null && !isNaN(v));

  // items2: 평균 요약
  const items2: SimpleItem[] = [
    { label: '관할 법원', value: name },
    { label: '평균 기일 간격', value: avgGap ? `${avgGap}일` : '-' },
  ];

  // items: 매각, 배당 순으로 나열
  const items: GridItem[] = [
    ...매각.map((v, idx) => ({
      label: `${idx + 1}회차 매각`,
      value: `${v}일`,
    })),
    ...배당.map((v, idx) => ({
      label: `${idx + 1}회차 배당`,
      value: `${v}일`,
    })),
  ];

  const handleDownloadExcel = () => {
    const allData = [...items2, ...items];

    const worksheetData = allData.map((item) => ({
      항목: item.label,
      내용: item.value,
    }));

    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    XLSX.writeFile(wb, `법원 평균 기일_배당 정보.xlsx`);
  };

  return (
    <Container>
      <Top>
        <h3>법원 평균 기일/배당 정보</h3>
        <s.Button
          onClick={handleDownloadExcel}
          disabled={isLoading || !isPremium}
          aria-label="엑셀 저장"
        >
          <img
            style={{ width: '18px' }}
            src="assets/images/icon/download-black.svg"
            alt="엑셀 저장"
          />
        </s.Button>
      </Top>

      <Description>
        <p>관할 법원의 평균 기일 간격 및</p>
        <p>낙찰된 물건의 유찰횟수별 소요 기간</p>
      </Description>

      {isLoading ? (
        <>
          <s.LoaderWrap>
            <s.LoaderImg src="/assets/images/icon/loader.gif" alt="로딩중" />
          </s.LoaderWrap>
        </>
      ) : (
        <>
          <s.SelectedOptionBox>
            <s.SelectedOption>{name}</s.SelectedOption>
          </s.SelectedOptionBox>
          <div style={{ gap: '24px', marginTop: '16px' }}>
            <SimpleInfoTable items={items2} />
            <div style={{ gap: '8px' }}>
              <h4>매각 물건 평균 소요 기간</h4>

              <GridTable items={items} maxColumns={10} />
            </div>
          </div>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  background-color: #fff;
  angle: 0 deg;
  opacity: 1;
  padding-top: 24px;
  padding-right: 16px;
  padding-bottom: 40px;
  padding-left: 16px;
  margin: 8px 0;
  gap: 8px;
  position: relative;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Description = styled.div`
  color: #787d87;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -2%;
  margin-bottom: 12px;

  p {
    margin: 0;
  }
`;
